import { Box, Breadcrumbs, Typography, Stack, Paper, Table, TableHead, TableRow, TableCell, TableBody, Button, Chip } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  listMyNotifications, markNotificationRead, markAllNotificationsRead, type InAppNotification, type NotificationSeverity,
} from '../api/notificari'

const SEVERITY_LABEL: Record<NotificationSeverity, string> = { Critical: 'Critice', Warning: 'Avertismente', Info: 'Informative' }
const SEVERITY_COLOR: Record<NotificationSeverity, 'error' | 'warning' | 'info'> = { Critical: 'error', Warning: 'warning', Info: 'info' }

export default function NotificarileMelePage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['notificari', 'mele'], queryFn: () => listMyNotifications() })

  const markRead = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['notificari'] }) },
  })
  const markAllRead = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['notificari'] }) },
  })

  const unreadCount = (data ?? []).filter((n) => n.readAt === null).length

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Administrare</Typography>
        <Typography variant="inherit" color="text.primary">Notificările mele</Typography>
      </Breadcrumbs>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h2">Notificările mele</Typography>
        <Button size="small" disabled={unreadCount === 0 || markAllRead.isPending} onClick={() => markAllRead.mutate()}>
          Marchează tot ca citit
        </Button>
      </Stack>

      {data && data.length === 0 && (
        <Box sx={{ color: 'text.secondary' }}>Nicio notificare.</Box>
      )}

      {data && data.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Severitate</TableCell><TableCell>Titlu</TableCell><TableCell>Detaliu</TableCell>
                <TableCell>Data</TableCell><TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((n: InAppNotification) => (
                <TableRow key={n.id} sx={{ opacity: n.readAt ? 0.6 : 1 }}>
                  <TableCell><Chip size="small" label={SEVERITY_LABEL[n.severity]} color={SEVERITY_COLOR[n.severity]} variant="outlined" /></TableCell>
                  <TableCell sx={{ fontWeight: n.readAt ? 400 : 600 }}>{n.title}</TableCell>
                  <TableCell>{n.detail}</TableCell>
                  <TableCell>{new Date(n.createdAt).toLocaleString('ro-RO')}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                      {!n.readAt && <Button size="small" onClick={() => markRead.mutate(n.id)}>Marchează citit</Button>}
                      {n.linkPath && <Button size="small" onClick={() => navigate(n.linkPath!)}>Deschide</Button>}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Stack>
  )
}
